/**
 * Generates a consistent color for a player based on their ID and optional position
 */

// Vibrant, distinct colors that work well for selection highlights
const PLAYER_COLORS = [
  { name: 'rose', tailwind: 'rose-500', hex: '#f43f5e' },
  { name: 'blue', tailwind: 'blue-500', hex: '#3b82f6' },
  { name: 'green', tailwind: 'green-500', hex: '#22c55e' },
  { name: 'purple', tailwind: 'purple-500', hex: '#a855f7' },
  { name: 'orange', tailwind: 'orange-500', hex: '#f97316' },
  { name: 'pink', tailwind: 'pink-500', hex: '#ec4899' },
  { name: 'cyan', tailwind: 'cyan-500', hex: '#06b6d4' },
  { name: 'amber', tailwind: 'amber-500', hex: '#f59e0b' },
  { name: 'lime', tailwind: 'lime-500', hex: '#84cc16' },
  { name: 'indigo', tailwind: 'indigo-500', hex: '#6366f1' },
  { name: 'teal', tailwind: 'teal-500', hex: '#14b8a6' },
  { name: 'fuchsia', tailwind: 'fuchsia-500', hex: '#d946ef' },
];

// Cache to store player ID -> color index mappings to ensure consistency across sessions
const playerColorCache = new Map<string, number>();
let nextColorIndex = 0;

/**
 * Get a consistent color for a player based on their ID
 * This uses a cache to ensure the same player always gets the same color
 * and new players get the next available color in sequence
 */
export function getPlayerColor(playerId: string): { name: string; tailwind: string; hex: string } {
  // Check cache first
  let colorIndex = playerColorCache.get(playerId);

  if (colorIndex === undefined) {
    // Assign next color in sequence
    colorIndex = nextColorIndex % PLAYER_COLORS.length;
    playerColorCache.set(playerId, colorIndex);
    nextColorIndex++;
  }

  return PLAYER_COLORS[colorIndex];
}

/**
 * Get Tailwind ring classes for a player's selection color
 */
export function getPlayerRingClasses(playerId: string): string {
  const color = getPlayerColor(playerId);
  return `ring-${color.tailwind}`;
}

/**
 * Get Tailwind background classes for a player's color
 */
export function getPlayerBgClasses(playerId: string): string {
  const color = getPlayerColor(playerId);
  return `bg-${color.tailwind}`;
}
